import clsx from 'clsx';
import React from 'react';

import styled from '@emotion/styled';

import InfoTip from '../Help/InfoTip';

interface StatsRootProps {
  children: React.ReactNode;
}

const StatsRoot = ({ children }: StatsRootProps) => {
  return <StatsRootStyle>{children}</StatsRootStyle>;
};

interface StatsHeaderProps {
  children: React.ReactNode;
  // Plain-words definition, shown from an ⓘ beside the label.
  hint?: React.ReactNode;
}

const StatsHeader = ({ children, hint }: StatsHeaderProps) => {
  return (
    <StatsHeaderStyle>
      {children}
      {hint && <InfoTip>{hint}</InfoTip>}
    </StatsHeaderStyle>
  );
};

interface StatsValueProps {
  children: React.ReactNode;
}

const StatsValue = ({ children }: StatsValueProps) => {
  return <StatsValueStyle>{children}</StatsValueStyle>;
};

interface DynamicStatsValueProps {
  children: any;
  trend: number;
}

const DynamicStatsValue = ({ children, trend }: DynamicStatsValueProps) => {
  return (
    <DynamicStatsValueStyle
      className={clsx(trend >= 0 ? 'positive' : 'negative')}
    >
      {children}
    </DynamicStatsValueStyle>
  );
};

interface DynamicStatsFooterProps {
  children: any;
  trend: number;
}

const DynamicStatsFooter = ({ children, trend }: DynamicStatsFooterProps) => {
  return (
    <DynamicStatsFooterStyle
      className={clsx(trend >= 0 ? 'positive' : 'negative')}
    >
      {children}
    </DynamicStatsFooterStyle>
  );
};

interface PlainFooterProps {
  children: React.ReactNode;
}

const PlainFooter = ({ children }: PlainFooterProps) => {
  return <PlainFooterStyled>{children}</PlainFooterStyled>;
};

const StatsCard = {
  Root: StatsRoot,
  Header: StatsHeader,
  Value: StatsValue,
  DynamicValue: DynamicStatsValue,
  DynamicFooter: DynamicStatsFooter,
  PlainFooter: PlainFooter,
};

export default StatsCard;

// Metric tile (Stripe/Mercury): small sentence-case label, one big
// tabular number, a quiet footer. Hairline card, no heavy shadow.
const StatsRootStyle = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: var(--radius);
  padding: 18px 20px;
  gap: 8px;
  background-color: white;
  box-shadow: var(--shadow-xs);
  min-width: 0;
`;

const StatsHeaderStyle = styled.div`
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  color: var(--muted);
`;

const StatsValueStyle = styled.div`
  font-family: var(--font-display);
  letter-spacing: -0.02em;
  font-size: 26px;
  line-height: 32px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.palette.HetheraBlack};
  overflow-wrap: anywhere;
`;

const DynamicStatsValueStyle = styled(StatsValueStyle)`
  display: grid;
  grid-template-columns: 24px auto;
  align-items: center;
  gap: 16px;

  &.positive {
    color: #1F7A45;
  }
  &.negative {
    color: #D92D20;
  }
`;

const PlainFooterStyled = styled.div`
  color: ${({ theme }) => theme.palette.mediumGrayText};
  font-size: 12.5px;
  line-height: 18px;
  font-weight: 400;
`;

// Trend reads as a small coloured chip, like Stripe's "+12.4%".
const DynamicStatsFooterStyle = styled(PlainFooterStyled)`
  width: fit-content;
  padding: 1px 8px;
  border-radius: 999px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;

  &:empty {
    display: none;
  }
  &.positive {
    color: #1f7a45;
    background: rgba(31, 122, 69, 0.08);
  }
  &.negative {
    color: #D92D20;
    background: rgba(217, 45, 32, 0.08);
  }
`;
